import React, { useCallback, useContext, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { BundledOfferState, OfferPageActions, OfferPageFilterType, PaginatorState, takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OfferCalendarDatePickerSelectBox from '../Filters/CalendarPicker/OfferCalendarDatePickerSelectBox'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import OfferCapacityWithAgesSelection from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelection'
import OfferCapacityWithAgesSelectBox from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { getComplementaryBundleState } from 'selectors/offerPage/bundledOfferPageSelectors'
import config from 'constants/config'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import OfferBundleAndSaveCalendarSidebar from '../Filters/BundleAndSaveCalendarSidebarPicker/OfferBundleAndSaveCalendarSidebar'
import OfferFilterBundleSidebarBottomPanel from '../Filters/Sidebar/OfferFilterBundleSidebarBottomPanel'
import useDelayedTogglingBoolean from 'hooks/useDelayedTogglingBoolean'

const FiltersBlock = styled.div`
  flex-grow: 1;

  > * {
    height: ${rem(64)};
    width: 100%;
    padding: 0 ${rem(24)};

    &:not(:first-child):not(:last-child) {
      box-shadow: 0 -1px 0px 0px ${props => props.theme.palette.neutral.default.five};
    }
  }
`

interface Props {
  className?: string;
  onViewPackageOptions?: () => void;
  activeFilter?: OfferPageFilterType;
  capacities?: Array<App.RoomCapacity>;
  paginator?: PaginatorState;
  complementaryBundleState?: BundledOfferState;
}

function BundleAndSaveSidebarFilters(props: Props) {
  const {
    onViewPackageOptions,
    activeFilter,
    capacities,
    paginator,
    complementaryBundleState,
  } = props

  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const formRef = useRef<HTMLFormElement>(null)

  const closeFilter = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      dispatch({ type: OfferPageActions.closeFilter, occupancyPicker: true })
      onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
    }
  }, [dispatch, onUserAnalyticsAction])

  const onBack = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      if (paginator?.backOfferId) {
        dispatch({ type: OfferPageActions.setPaginatorCurrentOfferId, currentOfferId: paginator?.backOfferId })
        return
      }

      dispatch({ type: OfferPageActions.toggleFilter, filter: 'all' })
    }
  }, [dispatch, paginator?.backOfferId])

  const onApply = useCallback(() => {
    let complementaryHaveSelected = false
    if (activeFilter === 'checkin') {
      complementaryHaveSelected = !complementaryBundleState?.checkInDate
    }
    if (complementaryBundleState?.offer && complementaryHaveSelected) {
      dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'duration' })
      dispatch({ type: OfferPageActions.setPaginatorCurrentOfferId, currentOfferId: complementaryBundleState.offer.id })
      return
    }

    closeFilter()
    onViewPackageOptions?.()
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.Apply)
  }, [activeFilter, complementaryBundleState, closeFilter, onViewPackageOptions, onUserAnalyticsAction, dispatch])

  // Workaround for bug in React Modal when quickly switching between open/closed
  // https://github.com/reactjs/react-modal/issues/888
  // https://github.com/reactjs/react-modal/issues/808
  // TODO: Come up with better solution, because this will always delay opening the modal
  const delayedOpen = useDelayedTogglingBoolean(!!activeFilter, { onDelay: 300 })

  return (
    <ModalBase
      isOpen={delayedOpen}
      onClose={closeFilter}
      mode="drawer"
    >
      <form onSubmit={onApply} ref={formRef}>
        {activeFilter === 'all' && <>
          <ModalHeader
            title={`Your ${config.brandConfig.holidaySynonym}`}
            onCloseButtonClick={closeFilter}
          />
          <ModalBody>
            <FiltersBlock>
              <OfferCapacityWithAgesSelectBox inSidebar multiRoomDisabled />
              <OfferCalendarDatePickerSelectBox inSidebar />
            </FiltersBlock>
          </ModalBody>
        </>}
        {activeFilter === 'capacity' && <>
          <ModalHeader
            title="Who is going?"
            onCloseButtonClick={closeFilter}
            onBackButtonClick={onBack}
          />
          <ModalBody>
            <OfferCapacityWithAgesSelection capacities={capacities} disableMultiRoom />
          </ModalBody>
          <OfferFilterBundleSidebarBottomPanel
            activeFilter={activeFilter}
          />
        </>}
        {activeFilter === 'checkin' && <OfferBundleAndSaveCalendarSidebar
          onClose={closeFilter}
          onBack={onBack}
          onApply={onApply}
        />}
      </form>
    </ModalBase>
  )
}

export default withOfferPageAndReduxState((currentState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    paginator: state.paginator,
    activeFilter: state.activeFilter,
    complementaryBundleState: getComplementaryBundleState(state),
  }
}, BundleAndSaveSidebarFilters)
